import axios, { Method } from 'axios'
import { useEffect, useState } from 'react'
import { getGatewayHttpUrl } from '../deviceGateway'
import { getCachedAuthToken } from '../../views/LiveView/api/getCachedAuthToken'

type UseDeviceGatewayApiOptions = {
    baseURL: string | null
    path: string
    method?: Method
    body?: Record<string, unknown>
    binary?: boolean
    enabled: boolean
    intervalMs?: number
    deviceName?: string | null
}

type UseDeviceGatewayApiResult<TResponse> = {
    data: TResponse | null
    loading: boolean
    error: unknown
}

export function useDeviceGatewayApi<TResponse = unknown>({
    baseURL,
    path,
    method = 'GET',
    body,
    binary = false,
    enabled,
    intervalMs,
    deviceName
}: UseDeviceGatewayApiOptions): UseDeviceGatewayApiResult<TResponse> {
    const [data, setData] = useState<TResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<unknown>(null)

    const bodyKey = body ? JSON.stringify(body) : ''

    useEffect(() => {
        if (!enabled || !baseURL) return

        let cancelled = false
        const controller = new AbortController()
        const baseUrlCleaned = baseURL.replace('https://', '').replace('http://', '')

        const fetchOnce = async () => {
            setLoading(true)
            try {
                const response = await axios.request({
                    method: 'POST',
                    url: getGatewayHttpUrl('/proxy'),
                    data: {
                        ip: baseUrlCleaned,
                        path,
                        method,
                        auth_token: localStorage.getItem('liveViewAccessToken') || '',
                        data: body ?? {},
                        device_name: deviceName || 'unknown name'
                    },
                    responseType: binary ? 'arraybuffer' : 'json',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: '*/*',
                        Authorization: getCachedAuthToken()
                    },
                    signal: controller.signal
                })
                if (cancelled) return
                setData(response.data as TResponse)
                setError(null)
            } catch (err) {
                if (cancelled || axios.isCancel(err)) return
                setError(err)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchOnce()
        const intervalId = intervalMs ? window.setInterval(fetchOnce, intervalMs) : undefined

        return () => {
            cancelled = true
            controller.abort()
            if (intervalId) window.clearInterval(intervalId)
        }
    }, [enabled, baseURL, path, method, binary, intervalMs, deviceName, bodyKey])

    return { data, loading, error }
}
